import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackInventoryError, trackInventoryEvent } from "../analytics/inventory";
import { useCreateInventoryItemMutation } from "./useCreateInventoryItemMutation";
import { useUpdateInventoryItemMutation } from "./useUpdateInventoryItemMutation";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { buildInventoryCreatePayload } from "../utils/inventoryPayloadBuilder";
import { readReturnPath } from "../utils/navigationOrigin";

interface SubmitInventoryByChatInput {
  data: Record<string, unknown>;
  file?: File;
}

export function useFormChatSubmitInventory(editItemId?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const productBase = useProductAnalyticsBase();
  const { mutateAsync } = useCreateInventoryItemMutation();
  const { mutateAsync: updateMutateAsync } = useUpdateInventoryItemMutation(
    editItemId ?? "",
  );
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};

  const submitInventoryByChat = useCallback(
    async ({ data, file }: SubmitInventoryByChatInput) => {
      const payload = buildInventoryCreatePayload({
        data,
        terreiroId,
        mediumId,
      });

      if (!payload) {
        throw new Error("No owner scope found for inventory creation.");
      }

      trackInventoryEvent(
        "form_chat_submitted",
        { ...productBase, trigger: "mutation" },
        { owner_scope: terreiroId ? "terreiro" : "personal", has_image: Boolean(file) },
      );

      try {
        await mutateAsync(file ? { ...payload, file } : payload);
        trackInventoryEvent(
          "form_chat_inventory_created",
          { ...productBase, trigger: "mutation" },
          { owner_scope: terreiroId ? "terreiro" : "personal" },
        );
        navigate("/inventory/list");
      } catch (error) {
        trackInventoryError(
          "form_chat_submit_error",
          { ...productBase, trigger: "mutation" },
          error,
          { owner_scope: terreiroId ? "terreiro" : "personal" },
        );
        throw error;
      }
    },
    [mediumId, mutateAsync, navigate, productBase, terreiroId],
  );

  const updateInventoryByChat = useCallback(
    async ({ data, file }: SubmitInventoryByChatInput) => {
      if (!editItemId) {
        throw new Error("No inventory item selected for update.");
      }
      const payload = buildInventoryCreatePayload({
        data,
        terreiroId,
        mediumId,
      });

      if (!payload) {
        throw new Error("No owner scope found for inventory update.");
      }

      trackInventoryEvent(
        "inventory_update_submit_attempt",
        { ...productBase, trigger: "mutation" },
        { item_id: editItemId, has_image: Boolean(file), source: "form_chat" },
      );

      try {
        await updateMutateAsync(file ? { ...payload, file } : payload);
        trackInventoryEvent(
          "inventory_item_updated",
          { ...productBase, trigger: "mutation" },
          { item_id: editItemId, source: "form_chat" },
        );
        navigate(readReturnPath(location.state) ?? `/inventory/${editItemId}`);
      } catch (error) {
        trackInventoryError(
          "inventory_update_error",
          { ...productBase, trigger: "mutation" },
          error,
          { item_id: editItemId, source: "form_chat" },
        );
        throw error;
      }
    },
    [editItemId, location.state, mediumId, navigate, productBase, terreiroId, updateMutateAsync],
  );

  return {
    submitInventoryByChat,
    updateInventoryByChat,
  };
}
